import React, { useEffect, useState, useCallback } from "react";
import { api, reportExportUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { formatRupiah, formatDate, PAYMENT_TYPE_LABEL, BILL_STATUS_META, REMINDER_META } from "@/lib/format";
import { FileBarChart, Download, FileDown, Loader2, Users2, Truck, ReceiptText, Wrench } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer } from "recharts";
import { useAutoRefresh } from "@/lib/autoRefresh";

const REMINDER_KEYS = [
  { value: "pajak", label: "Pajak" }, { value: "kir", label: "KIR" },
  { value: "servis", label: "Servis" }, { value: "oli", label: "Ganti Oli" },
];

function todayISO() { return new Date().toISOString().slice(0, 10); }
function monthStartISO() { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }

function PeriodFilter({ start, end, setStart, setEnd }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <Label className="text-xs">Tanggal Mulai</Label>
        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 h-9" data-testid="report-start-date" />
      </div>
      <div>
        <Label className="text-xs">Tanggal Akhir</Label>
        <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-9" data-testid="report-end-date" />
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" data-testid="preset-today"
          onClick={() => { setStart(todayISO()); setEnd(todayISO()); }}>Hari Ini</Button>
        <Button type="button" variant="outline" size="sm" data-testid="preset-month"
          onClick={() => { setStart(monthStartISO()); setEnd(todayISO()); }}>Bulan Ini</Button>
        <Button type="button" variant="ghost" size="sm" data-testid="preset-all"
          onClick={() => { setStart(""); setEnd(""); }}>Semua</Button>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, tone = "brand", testId }) {
  const toneCls = {
    brand: "text-brand", rose: "text-rose-600", amber: "text-amber-700", emerald: "text-emerald-700",
  }[tone];
  return (
    <div className="rounded-xl border bg-white p-4" data-testid={testId}>
      <p className="text-[11px] font-mono uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-lg font-bold font-mono ${toneCls}`}>{value}</p>
      {sub != null && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function MonthlyChart({ data, bars }) {
  if (!data || data.length === 0) return null;
  return (
    <Card className="p-4" data-testid="report-chart">
      <div style={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" fontSize={11} />
            <YAxis width={44} fontSize={11} tickFormatter={(v) => (v >= 1e6 ? `${(v / 1e6).toFixed(0)}jt` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}rb` : v)} />
            <Tooltip formatter={(v) => formatRupiah(v)} />
            <Legend />
            {bars.map((b) => <Bar key={b.key} dataKey={b.key} name={b.name} fill={b.color} radius={[3, 3, 0, 0]} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

/* ---------------- Cash report ---------------- */
function CashReport({ branches, canPickBranch }) {
  const [start, setStart] = useState(monthStartISO());
  const [end, setEnd] = useState(todayISO());
  const [branch, setBranch] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback((silent) => {
    if (silent !== true) setLoading(true);
    api.get("/reports/cash", { params: {
      start_date: start || undefined, end_date: end || undefined,
      branch: canPickBranch && branch !== "all" ? branch : undefined,
    } }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [start, end, branch, canPickBranch]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);

  const doExport = (fmt) => window.open(reportExportUrl("cash", {
    start_date: start || undefined, end_date: end || undefined,
    branch: canPickBranch && branch !== "all" ? branch : undefined,
    fmt: fmt === "pdf" ? "pdf" : undefined,
  }), "_blank");

  const rows = data?.rows || [];
  const t = data?.totals || {};
  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <PeriodFilter start={start} end={end} setStart={setStart} setEnd={setEnd} />
          <div className="flex items-end gap-3">
            {canPickBranch && (
              <div>
                <Label className="text-xs">Cabang</Label>
                <Select value={branch} onValueChange={setBranch}>
                  <SelectTrigger className="mt-1 h-9 w-44" data-testid="cash-branch-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Cabang</SelectItem>
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={() => doExport("excel")} className="bg-brand hover:bg-emerald-800 h-9" data-testid="cash-export-btn">
                <Download className="h-4 w-4 mr-2" /> Excel
              </Button>
              <Button variant="outline" onClick={() => doExport("pdf")} className="border-brand text-brand h-9" data-testid="cash-export-pdf-btn">
                <FileDown className="h-4 w-4 mr-2" /> PDF
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total Diajukan" value={formatRupiah(t.total_requested || 0)} testId="cash-stat-requested" />
        <StatCard label="Total Dicairkan" value={formatRupiah(t.total_disbursed || 0)} tone="emerald" testId="cash-stat-disbursed" />
        <StatCard label="Total Bukti" value={formatRupiah(t.total_expensed || 0)} tone="brand" testId="cash-stat-expensed" />
        <StatCard label="Belum Dipertanggungjawabkan" value={formatRupiah(t.outstanding || 0)} tone="rose" testId="cash-stat-outstanding" />
      </div>

      <MonthlyChart data={data?.monthly} bars={[
        { key: "requested", name: "Diajukan", color: "#0ea5e9" },
        { key: "disbursed", name: "Dicairkan", color: "#059669" },
      ]} />

      <Card className="p-0 overflow-hidden">
        {loading ? <div className="p-6 space-y-2.5" data-testid="report-loading">{[0, 1, 2, 3, 4].map((i) => (<div key={i} className={`h-4 rounded-md skeleton-shimmer animate-fade-in-up stagger-${i + 1}`} style={{ width: i % 2 ? "100%" : "78%" }} />))}</div> : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6 text-center" data-testid="cash-empty">Tidak ada data pada periode ini.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cash-report-table">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5">Pemohon</th>
                  <th className="text-left px-4 py-2.5">Cabang</th>
                  <th className="text-left px-4 py-2.5">Divisi</th>
                  <th className="text-right px-4 py-2.5">Diajukan</th>
                  <th className="text-right px-4 py-2.5">Dicairkan</th>
                  <th className="text-right px-4 py-2.5">Bukti</th>
                  <th className="text-right px-4 py-2.5">Sisa</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.requester_id || i} className="border-t" data-testid={`cash-row-${i}`}>
                    <td className="px-4 py-2.5 font-medium">{r.requester_name}</td>
                    <td className="px-4 py-2.5">{r.branch_name || "-"}</td>
                    <td className="px-4 py-2.5">{r.divisi || "-"}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{formatRupiah(r.total_requested)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{formatRupiah(r.total_disbursed)}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{formatRupiah(r.total_expensed)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-rose-600 font-semibold">{formatRupiah(r.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Vendor report ---------------- */
function VendorReport({ initialCompany }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [ptype, setPtype] = useState("all");
  const [company, setCompany] = useState(initialCompany || "all");
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}); }, []);

  const load = useCallback((silent) => {
    if (silent !== true) setLoading(true);
    api.get("/reports/vendor", { params: {
      start_date: start || undefined, end_date: end || undefined,
      payment_type: ptype !== "all" ? ptype : undefined,
      company: company !== "all" ? company : undefined,
    } }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [start, end, ptype, company]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);

  const doExport = (fmt) => window.open(reportExportUrl("vendor", {
    start_date: start || undefined, end_date: end || undefined,
    payment_type: ptype !== "all" ? ptype : undefined,
    company: company !== "all" ? company : undefined,
    fmt: fmt === "pdf" ? "pdf" : undefined,
  }), "_blank");

  const rows = data?.rows || [];
  const s = data?.summary || {};
  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <PeriodFilter start={start} end={end} setStart={setStart} setEnd={setEnd} />
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Per PT</Label>
              <Select value={company} onValueChange={setCompany}>
                <SelectTrigger className="mt-1 h-9 w-48" data-testid="vendor-company-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua PT</SelectItem>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  <SelectItem value="none">Tanpa PT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Jenis Pembayaran</Label>
              <Select value={ptype} onValueChange={setPtype}>
                <SelectTrigger className="mt-1 h-9 w-40" data-testid="vendor-ptype-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Jenis</SelectItem>
                  <SelectItem value="harian">Harian</SelectItem>
                  <SelectItem value="jatuh_tempo">Jatuh Tempo</SelectItem>
                  <SelectItem value="bilyet_giro">Bilyet Giro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => doExport("excel")} className="bg-brand hover:bg-emerald-800 h-9" data-testid="vendor-export-btn">
                <Download className="h-4 w-4 mr-2" /> Excel
              </Button>
              <Button variant="outline" onClick={() => doExport("pdf")} className="border-brand text-brand h-9" data-testid="vendor-export-pdf-btn">
                <FileDown className="h-4 w-4 mr-2" /> PDF
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Sudah Dibayar" value={formatRupiah(s.paid_total || 0)} sub={`${s.paid_count || 0} tagihan`} tone="emerald" testId="vendor-stat-paid" />
        <StatCard label="Belum Dibayar" value={formatRupiah(s.unpaid_total || 0)} sub={`${s.unpaid_count || 0} tagihan`} tone="rose" testId="vendor-stat-unpaid" />
        <StatCard label="BG ≤ 7 Hari" value={formatRupiah(s.bg7_total || 0)} sub={`${s.bg7_count || 0} BG`} tone="amber" testId="vendor-stat-bg7" />
        <StatCard label="BG ≤ 30 Hari" value={formatRupiah(s.bg30_total || 0)} sub={`${s.bg30_count || 0} BG`} tone="amber" testId="vendor-stat-bg30" />
      </div>

      <MonthlyChart data={data?.monthly} bars={[
        { key: "paid", name: "Dibayar", color: "#059669" },
        { key: "unpaid", name: "Belum Dibayar", color: "#e11d48" },
      ]} />

      <Card className="p-0 overflow-hidden">
        {loading ? <div className="p-6 space-y-2.5" data-testid="report-loading">{[0, 1, 2, 3, 4].map((i) => (<div key={i} className={`h-4 rounded-md skeleton-shimmer animate-fade-in-up stagger-${i + 1}`} style={{ width: i % 2 ? "100%" : "78%" }} />))}</div> : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6 text-center" data-testid="vendor-empty">Tidak ada tagihan pada periode ini.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vendor-report-table">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5">PT</th>
                  <th className="text-left px-4 py-2.5">Vendor</th>
                  <th className="text-left px-4 py-2.5">Jenis</th>
                  <th className="text-right px-4 py-2.5">Jumlah</th>
                  <th className="text-left px-4 py-2.5">Jatuh Tempo</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-right px-4 py-2.5">Sisa Hari</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const meta = BILL_STATUS_META[r.status] || {};
                  return (
                    <tr key={r.id || i} className="border-t" data-testid={`vendor-row-${i}`}>
                      <td className="px-4 py-2.5">{r.company_name || <span className="text-muted-foreground">Tanpa PT</span>}</td>
                      <td className="px-4 py-2.5 font-medium">{r.vendor_name}</td>
                      <td className="px-4 py-2.5">{PAYMENT_TYPE_LABEL[r.payment_type] || r.payment_type}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{formatRupiah(r.amount)}</td>
                      <td className="px-4 py-2.5">{formatDate(r.due_date)}</td>
                      <td className="px-4 py-2.5"><Badge variant="outline" className={meta.cls}>{meta.label || r.status}</Badge></td>
                      <td className="px-4 py-2.5 text-right font-mono">{r.status === "paid" ? "-" : (r.days_to_due != null ? `${r.days_to_due} hari` : "-")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Vehicle report ---------------- */
function VehicleReport({ branches }) {
  const [branch, setBranch] = useState("all");
  const [reminder, setReminder] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback((silent) => {
    if (silent !== true) setLoading(true);
    api.get("/reports/vehicles", { params: {
      branch: branch !== "all" ? branch : undefined,
      reminder: reminder !== "all" ? reminder : undefined,
    } }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [branch, reminder]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);

  const doExport = (fmt) => window.open(reportExportUrl("vehicles", {
    branch: branch !== "all" ? branch : undefined,
    reminder: reminder !== "all" ? reminder : undefined,
    fmt: fmt === "pdf" ? "pdf" : undefined,
  }), "_blank");

  const rows = data?.rows || [];
  const attention = [...(data?.overdue || []), ...(data?.upcoming || [])];
  const cell = (rem) => {
    if (!rem) return <span className="text-muted-foreground">-</span>;
    const meta = REMINDER_META[rem.status] || {};
    return (
      <div className="flex flex-col gap-0.5">
        <Badge variant="outline" className={`${meta.cls} w-fit`}>{meta.label || rem.status}</Badge>
        {rem.due_date && <span className="text-[11px] text-muted-foreground">{formatDate(rem.due_date)}</span>}
      </div>
    );
  };
  const findRem = (r, key) => r.reminders.find((x) => x.key === key);

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex items-end gap-3">
            <div>
              <Label className="text-xs">Cabang</Label>
              <Select value={branch} onValueChange={setBranch}>
                <SelectTrigger className="mt-1 h-9 w-44" data-testid="vehicle-branch-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Cabang</SelectItem>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Jenis Reminder</Label>
              <Select value={reminder} onValueChange={setReminder}>
                <SelectTrigger className="mt-1 h-9 w-40" data-testid="vehicle-reminder-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Reminder</SelectItem>
                  {REMINDER_KEYS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => doExport("excel")} className="bg-brand hover:bg-emerald-800 h-9" data-testid="vehicle-export-btn">
              <Download className="h-4 w-4 mr-2" /> Excel
            </Button>
            <Button variant="outline" onClick={() => doExport("pdf")} className="border-brand text-brand h-9" data-testid="vehicle-export-pdf-btn">
              <FileDown className="h-4 w-4 mr-2" /> PDF
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Total Kendaraan" value={data?.total_vehicles || 0} testId="vehicle-stat-total" />
        <StatCard label="Lewat Jatuh Tempo" value={data?.overdue_count || 0} tone="rose" testId="vehicle-stat-overdue" />
        <StatCard label="≤ 30 Hari" value={data?.upcoming_count || 0} tone="amber" testId="vehicle-stat-upcoming" />
      </div>

      {attention.length > 0 && (
        <Card className="p-4" data-testid="vehicle-attention">
          <h4 className="font-semibold text-sm mb-3">Perlu Perhatian (lewat / akan jatuh tempo ≤ 30 hari)</h4>
          <ul className="space-y-2">
            {attention.map((e, i) => {
              const meta = REMINDER_META[e.status] || {};
              return (
                <li key={`${e.id}-${e.key}-${i}`} className="flex items-center justify-between gap-3 text-sm border rounded-lg px-3 py-2" data-testid={`vehicle-attention-${i}`}>
                  <div className="min-w-0">
                    <span className="font-medium">{e.label}</span> · {e.vehicle_type} {e.plate_number}
                    <span className="text-muted-foreground"> · {e.branch}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-muted-foreground">{formatDate(e.due_date)}</span>
                    <Badge variant="outline" className={meta.cls}>{e.days < 0 ? `${Math.abs(e.days)} hari lewat` : `${e.days} hari lagi`}</Badge>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        {loading ? <div className="p-6 space-y-2.5" data-testid="report-loading">{[0, 1, 2, 3, 4].map((i) => (<div key={i} className={`h-4 rounded-md skeleton-shimmer animate-fade-in-up stagger-${i + 1}`} style={{ width: i % 2 ? "100%" : "78%" }} />))}</div> : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6 text-center" data-testid="vehicle-empty">Belum ada data kendaraan.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vehicle-report-table">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5">Kendaraan</th>
                  <th className="text-left px-4 py-2.5">Cabang</th>
                  <th className="text-left px-4 py-2.5">Pajak</th>
                  <th className="text-left px-4 py-2.5">KIR</th>
                  <th className="text-left px-4 py-2.5">Servis</th>
                  <th className="text-left px-4 py-2.5">Ganti Oli</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i} className="border-t align-top" data-testid={`vehicle-row-${i}`}>
                    <td className="px-4 py-2.5 font-medium">{r.vehicle_type}<br /><span className="text-xs font-mono text-muted-foreground">{r.plate_number}</span></td>
                    <td className="px-4 py-2.5">{r.branch}</td>
                    <td className="px-4 py-2.5">{cell(findRem(r, "pajak"))}</td>
                    <td className="px-4 py-2.5">{cell(findRem(r, "kir"))}</td>
                    <td className="px-4 py-2.5">{cell(findRem(r, "servis"))}</td>
                    <td className="px-4 py-2.5">{cell(findRem(r, "oli"))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Maintenance (perawatan) report ---------------- */
function MaintenanceReport({ branches, canPickBranch, initialItem }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [branch, setBranch] = useState("all");
  const [item, setItem] = useState(REMINDER_KEYS.some((k) => k.value === initialItem) ? initialItem : "all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback((silent) => {
    if (silent !== true) setLoading(true);
    api.get("/reports/maintenance", { params: {
      start_date: start || undefined, end_date: end || undefined,
      branch: canPickBranch && branch !== "all" ? branch : undefined,
      item: item !== "all" ? item : undefined,
    } }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [start, end, branch, item, canPickBranch]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);

  const doExport = (fmt) => window.open(reportExportUrl("maintenance", {
    start_date: start || undefined, end_date: end || undefined,
    branch: canPickBranch && branch !== "all" ? branch : undefined,
    item: item !== "all" ? item : undefined,
    fmt: fmt === "pdf" ? "pdf" : undefined,
  }), "_blank");

  const rows = data?.rows || [];
  const s = data?.summary || {};
  const bt = s.by_type || {};

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <PeriodFilter start={start} end={end} setStart={setStart} setEnd={setEnd} />
          <div className="flex items-end gap-3">
            {canPickBranch && (
              <div>
                <Label className="text-xs">Cabang</Label>
                <Select value={branch} onValueChange={setBranch}>
                  <SelectTrigger className="mt-1 h-9 w-44" data-testid="maint-branch-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Cabang</SelectItem>
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs">Jenis Item</Label>
              <Select value={item} onValueChange={setItem}>
                <SelectTrigger className="mt-1 h-9 w-40" data-testid="maint-item-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Item</SelectItem>
                  {REMINDER_KEYS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => doExport("excel")} className="bg-brand hover:bg-emerald-800 h-9" data-testid="maint-export-btn">
                <Download className="h-4 w-4 mr-2" /> Excel
              </Button>
              <Button variant="outline" onClick={() => doExport("pdf")} className="border-brand text-brand h-9" data-testid="maint-export-pdf-btn">
                <FileDown className="h-4 w-4 mr-2" /> PDF
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total Biaya Perawatan" value={formatRupiah(s.total_cost || 0)} sub={`${s.total_count || 0} pengerjaan`} tone="brand" testId="maint-stat-total" />
        <StatCard label="Pajak" value={formatRupiah(bt.pajak?.cost || 0)} sub={`${bt.pajak?.count || 0}x`} tone="emerald" testId="maint-stat-pajak" />
        <StatCard label="KIR" value={formatRupiah(bt.kir?.cost || 0)} sub={`${bt.kir?.count || 0}x`} tone="amber" testId="maint-stat-kir" />
        <StatCard label="Servis + Oli" value={formatRupiah((bt.servis?.cost || 0) + (bt.oli?.cost || 0))} sub={`${(bt.servis?.count || 0) + (bt.oli?.count || 0)}x`} tone="brand" testId="maint-stat-servis-oli" />
      </div>

      <MonthlyChart data={data?.monthly} bars={[{ key: "cost", name: "Biaya Perawatan", color: "#059669" }]} />

      <Card className="p-0 overflow-hidden">
        {loading ? <div className="p-6 space-y-2.5" data-testid="report-loading">{[0, 1, 2, 3, 4].map((i) => (<div key={i} className={`h-4 rounded-md skeleton-shimmer animate-fade-in-up stagger-${i + 1}`} style={{ width: i % 2 ? "100%" : "78%" }} />))}</div> : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6 text-center" data-testid="maint-empty">Belum ada catatan perawatan pada periode ini.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="maint-report-table">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5">Tanggal</th>
                  <th className="text-left px-4 py-2.5">Kendaraan</th>
                  <th className="text-left px-4 py-2.5">Cabang</th>
                  <th className="text-left px-4 py-2.5">Item</th>
                  <th className="text-right px-4 py-2.5">Biaya</th>
                  <th className="text-left px-4 py-2.5">Jatuh Tempo Berikutnya</th>
                  <th className="text-left px-4 py-2.5">Oleh</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.vehicle_id}-${i}`} className="border-t" data-testid={`maint-row-${i}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(r.date)}</td>
                    <td className="px-4 py-2.5 font-medium">{r.vehicle_type}<br /><span className="text-xs font-mono text-muted-foreground">{r.plate_number}</span></td>
                    <td className="px-4 py-2.5">{r.branch}</td>
                    <td className="px-4 py-2.5"><Badge variant="outline" className="border-brand text-brand">{r.label}</Badge></td>
                    <td className="px-4 py-2.5 text-right font-mono">{formatRupiah(r.actual_cost)}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{r.next_due ? formatDate(r.next_due) : "-"}</td>
                    <td className="px-4 py-2.5">{r.done_by || "-"}</td>
                  </tr>
                ))}
                <tr className="border-t bg-secondary/60 font-semibold">
                  <td className="px-4 py-2.5" colSpan={4}>TOTAL</td>
                  <td className="px-4 py-2.5 text-right font-mono text-brand">{formatRupiah(s.total_cost || 0)}</td>
                  <td className="px-4 py-2.5" colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const role = user?.role;
  const isApprover = role === "approver";
  const isKendaraan = role === "tim_kendaraan";
  const canAll = ["direktur", "tim_keuangan", "it_admin"].includes(role);
  const canMaint = ["tim_kendaraan", "direktur", "it_admin"].includes(role);
  const maintPickBranch = canAll || (isKendaraan && !user?.branch_id);
  const [branches, setBranches] = useState([]);
  const [params] = useSearchParams();
  const tabs = ["kas", "vendor", "kendaraan", ...(canMaint ? ["perawatan"] : [])];
  const tab = tabs.includes(params.get("tab")) ? params.get("tab") : "kas";
  const initialItem = params.get("item");

  useEffect(() => {
    if (canAll || maintPickBranch) api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, [canAll, maintPickBranch]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <FileBarChart className="h-6 w-6 text-brand" /> {isKendaraan ? "Laporan Perawatan" : "Laporan Keuangan"}
        </h2>
        <p className="text-sm text-muted-foreground">
          {isKendaraan
            ? "Rekap biaya perawatan kendaraan (pajak, KIR, servis, oli) — bisa diekspor ke Excel & PDF"
            : "Rekap kas per pemohon, tagihan vendor, status & biaya perawatan kendaraan — bisa diekspor ke Excel & PDF"}
        </p>
      </div>

      {isApprover ? (
        <CashReport branches={branches} canPickBranch={false} />
      ) : isKendaraan ? (
        <MaintenanceReport branches={branches} canPickBranch={maintPickBranch} initialItem={initialItem} />
      ) : (
        <Tabs key={`${tab}-${initialItem}-${params.get("company")}`} defaultValue={tab} className="space-y-4">
          <TabsList data-testid="report-tabs">
            <TabsTrigger value="kas" data-testid="tab-kas"><Users2 className="h-4 w-4 mr-1.5" /> Kas per Pemohon</TabsTrigger>
            <TabsTrigger value="vendor" data-testid="tab-vendor"><ReceiptText className="h-4 w-4 mr-1.5" /> Tagihan Vendor</TabsTrigger>
            <TabsTrigger value="kendaraan" data-testid="tab-kendaraan"><Truck className="h-4 w-4 mr-1.5" /> Kendaraan</TabsTrigger>
            {canMaint && <TabsTrigger value="perawatan" data-testid="tab-perawatan"><Wrench className="h-4 w-4 mr-1.5" /> Perawatan</TabsTrigger>}
          </TabsList>
          <TabsContent value="kas"><CashReport branches={branches} canPickBranch={canAll} /></TabsContent>
          <TabsContent value="vendor"><VendorReport initialCompany={params.get("company")} /></TabsContent>
          <TabsContent value="kendaraan"><VehicleReport branches={branches} /></TabsContent>
          {canMaint && <TabsContent value="perawatan"><MaintenanceReport branches={branches} canPickBranch={canAll} initialItem={initialItem} /></TabsContent>}
        </Tabs>
      )}
    </div>
  );
}
